import { isAlbum, isCalendar } from "../../_prism-builder/trays/tray-determiner";
import { builderDispatch, WebUI } from "../ui";
import { db, canUseIndexedDB, handleDexieError } from "./db";
import { usePrismItemStore } from 'stores/usePrismItemStore';

/**
 * Uploads a file to the API.
 * @param {Object} upload - The upload object.
 * @param {File} compressedBlobAsFile - The compressed blob as a file.
 * @returns {Promise<void>} - A promise that resolves when the file is uploaded.
 */
export async function uploadFileToAPI(upload, compressedBlobAsFile, prismItem = null) {
  const formData = buildFormData(compressedBlobAsFile, { uploadSource: 'uploadIndexedDB', target_album: null });
  formData.append('name', compressedBlobAsFile.name);
  formData.append('file', compressedBlobAsFile);
  const canKeepUsingIndexedDB = await canUseIndexedDB(1000 * 1024 * 1024);
  
  let number_of_files = 0;
  let number_of_errors = 0;

  try {
    const response = await fetch(
      prismItem
        ? `/edt/img/add/${prismItem.sec}/${prismItem.num}?json=1`
        : upload.uploadUrl,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (response.status !== 200) {
      number_of_files--;
      number_of_errors++;

      db.imageUpload.delete(upload.id).catch((e) => {
        return handleDexieError(e, { from: "uploadFileToAPI" });
      });

      console.error('Error posting files to API', response);
    }

    number_of_files++;
  } catch (error) {
    number_of_files--;
    number_of_errors++;

    db.imageUpload.delete(upload.id).catch((e) => {
      return handleDexieError(e, { from: "catch uploadFileToAPI" });
    });

    console.error('Error posting files to API', error);
  }
  
  if (number_of_errors > 0) {
    usePrismItemStore.setState(({ failedImagesUploaded}) => ({
      failedImagesUploaded: [
        ...failedImagesUploaded,
        compressedBlobAsFile.name,
      ],
    }));
  } else {
    let prismItemIDs = [...new Set(upload.prismItemIDs || [])];

    if (prismItem) {
      if (!prismItemIDs.includes(prismItem.num)) {
        prismItemIDs.push(prismItem.num);
      }
      builderDispatch.dispatch({
        type: WebUI.UPLOAD_PROGRESS,
      });
      usePrismItemStore.getState().trackProgress();
    }

    
    const canPersist =
      window?.featureFlags?.show_recent_uploads &&
      canKeepUsingIndexedDB &&
      !isAlbum() &&
      !isCalendar();      

    if (upload.idbSkipped) {
      builderDispatch.dispatch({ type: WebUI.UPLOAD_PROGRESS });
      usePrismItemStore.getState().trackProgress();
    } else if (canPersist) {
      db.imageUpload
        .update(upload.id, {
          status: 'done',
          prismItemIDs,
        })
        .catch((e) => handleDexieError(e, { from: 'update DONE' }));
    } else {
      builderDispatch.dispatch({ type: WebUI.UPLOAD_PROGRESS });
      usePrismItemStore.getState().trackProgress();
      db.imageUpload.delete(upload.id).catch((e) =>
        handleDexieError(e, {
          from: `canKeepUsingIndexedDB is ${canKeepUsingIndexedDB}`,
        })
      );
    }
  }
}

/**
 * Builds a FormData object with the provided file and additional data.
 * @param {File} file - The file to be included in the FormData object.
 * @param {Object} additionalData - Additional data to be included in the FormData object.
 * @returns {FormData} The FormData object containing the file and additional data.
 */
export function buildFormData(file, additionalData) {
  const formData = new FormData();
  const {
    uploadSource: uploadSourceOverride,
    ...otherParams
  } = additionalData;

  const uploadSource = uploadSourceOverride || 'livelink';

  formData.set('source', uploadSource);
  Object.entries(otherParams).forEach(([key, value]) => {
    formData.append(key, value);
  });

  return formData;
}
